import { Box3, Matrix3, Matrix4, Vector3 } from 'three';

/** brepsmith's row-major 3x3 as a rotation. */
export function orientRotation(rotationMatrix: readonly number[]): Matrix4 {
  const [a, b, c, d, e, f, g, h, i] = rotationMatrix;
  return new Matrix4().set(a, b, c, 0, d, e, f, 0, g, h, i, 0, 0, 0, 0, 1);
}

/**
 * A direction in the UCS-turned frame the editor shows, expressed in the customer's
 * own frame: the rotation part of `ucsTurn` undone. `ucsTurn` is identity when the UCS is off.
 */
export function toCustomerFrame(
  direction: readonly [number, number, number],
  ucsTurn: Matrix4,
): [number, number, number] {
  const undoTurn = new Matrix3().setFromMatrix4(ucsTurn).transpose();
  const v = new Vector3(direction[0], direction[1], direction[2]).applyMatrix3(undoTurn);
  return [v.x, v.y, v.z];
}

export interface PrintOrientationMatrices {
  /** For the CAD result groups, which arrive in the customer frame. */
  results: Matrix4;
  /** For the FEA field meshes, which are already in the UCS-turned frame. */
  fields: Matrix4;
  /** Scene bounds of the CAD result once oriented. */
  bounds: Box3;
}

/**
 * Turns the result into print orientation about its own centre, then lifts it
 * so it rests on the floor it rested on before. `measure` returns the scene
 * bounds of the CAD result when its group carries the given matrix.
 */
export function printOrientationMatrices(
  rotationMatrix: readonly number[],
  ucsTurn: Matrix4,
  measure: (groupMatrix: Matrix4) => Box3,
): PrintOrientationMatrices {
  const before = measure(ucsTurn);
  const pivot = before.isEmpty() ? new Vector3() : before.getCenter(new Vector3());
  const turnAbout = new Matrix4()
    .makeTranslation(pivot.x, pivot.y, pivot.z)
    .multiply(orientRotation(rotationMatrix))
    .multiply(new Matrix4().makeTranslation(-pivot.x, -pivot.y, -pivot.z));

  const rotated = measure(turnAbout.clone().multiply(ucsTurn));
  const liftZ = before.isEmpty() || rotated.isEmpty() ? 0 : before.min.z - rotated.min.z;
  const lift = new Matrix4().makeTranslation(0, 0, liftZ);

  const fields = lift.clone().multiply(turnAbout);
  const bounds = rotated.isEmpty() ? rotated : rotated.clone().translate(new Vector3(0, 0, liftZ));
  return { results: fields.clone().multiply(ucsTurn), fields, bounds };
}

/** A secondary axis is named once it carries at least this share of the direction. */
const SECONDARY_AXIS_MIN = 0.35;

/**
 * The axis a direction points along, e.g. "≈ −Y", or the two it sits between
 * ("≈ +Y / +Z") — easier to read than the unit vector itself.
 */
export function describeDirection(direction: readonly [number, number, number]): string {
  const axes = ['X', 'Y', 'Z']
    .map((name, i) => ({ name, value: direction[i] }))
    .sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
  if (Math.abs(axes[0].value) < 1e-9) return '—';
  const signed = (axis: { name: string; value: number }): string =>
    `${axis.value < 0 ? '−' : '+'}${axis.name}`;
  return Math.abs(axes[1].value) >= SECONDARY_AXIS_MIN
    ? `≈ ${signed(axes[0])} / ${signed(axes[1])}`
    : `≈ ${signed(axes[0])}`;
}
